import { useState, useEffect, useCallback } from 'react'
import { useGitHubToken } from './useGitHubToken'
import { graphqlRequest } from '../lib/graphql'

interface Repo {
  id: number
  name: string
  full_name: string
  description?: string
  html_url: string
  homepage?: string
  topics: string[]
  language?: string
  stargazers_count: number
  forks_count: number
  archived: boolean
  disabled: boolean
}

interface RepoList {
  total: number
  page: number
  per_page: number
  items: Repo[]
  languages: string[]
  topics: string[]
}

interface UseReposParams {
  page: number
  perPage: number
  sort: 'stars' | 'updated'
  language?: string
  topic?: string
  includePrivate: boolean
  search?: string
}

const REPOS_QUERY = `
query ReposList(
  $page: Int!
  $perPage: Int!
  $sort: RepositorySort!
  $language: String
  $topic: String
  $includePrivate: Boolean!
  $search: String
) {
  repos(
    page: $page
    perPage: $perPage
    sort: $sort
    language: $language
    topic: $topic
    includePrivate: $includePrivate
    search: $search
  ) {
    total
    page
    perPage
    items {
      id
      name
      fullName
      description
      htmlUrl
      homepage
      topics
      language
      stargazersCount
      forksCount
      archived
      disabled
    }
    languages
    topics
  }
}
`

type RepoGQL = {
  id: number
  name: string
  fullName: string
  description?: string | null
  htmlUrl: string
  homepage?: string | null
  topics: string[]
  language?: string | null
  stargazersCount: number
  forksCount: number
  archived: boolean
  disabled: boolean
}

type ReposData = {
  repos: {
    total: number
    page: number
    perPage: number
    items: RepoGQL[]
    languages: string[]
    topics: string[]
  }
}

function mapItem(r: RepoGQL): Repo {
  return {
    id: r.id,
    name: r.name,
    full_name: r.fullName,
    description: r.description ?? undefined,
    html_url: r.htmlUrl,
    homepage: r.homepage ?? undefined,
    topics: r.topics,
    language: r.language ?? undefined,
    stargazers_count: r.stargazersCount,
    forks_count: r.forksCount,
    archived: r.archived,
    disabled: r.disabled,
  }
}

function sortToGql(sort: 'stars' | 'updated'): 'STARS' | 'UPDATED' {
  return sort === 'updated' ? 'UPDATED' : 'STARS'
}

export function useRepos(params: UseReposParams) {
  const { token: githubToken } = useGitHubToken()
  const [data, setData] = useState<RepoList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)

      const headers: HeadersInit = {}
      if (githubToken) {
        headers['X-GitHub-Token'] = githubToken
      }

      let result: ReposData
      try {
        result = await graphqlRequest<ReposData>(
          REPOS_QUERY,
          {
            page: params.page,
            perPage: params.perPage,
            sort: sortToGql(params.sort),
            language: params.language || null,
            topic: params.topic || null,
            includePrivate: params.includePrivate,
            search: params.search || null,
          },
          headers,
          { signal: controller.signal }
        )
        clearTimeout(timeoutId)
      } catch (fetchError) {
        clearTimeout(timeoutId)
        if (fetchError instanceof TypeError) {
          if (fetchError.message.includes('fetch') || fetchError.message.includes('Failed to fetch')) {
            throw new Error('NetworkError: cannot reach backend.')
          }
        }
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new Error(`Timeout: request exceeded 30s.`)
        }
        throw fetchError
      }

      const raw = result.repos
      setData({
        total: raw.total,
        page: raw.page,
        per_page: raw.perPage,
        items: raw.items.map(mapItem),
        languages: raw.languages,
        topics: raw.topics,
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Load error'
      setError(errorMessage)
      setData({
        total: 0,
        page: 1,
        per_page: params.perPage,
        items: [],
        languages: [],
        topics: [],
      })
    } finally {
      setLoading(false)
    }
  }, [params.page, params.perPage, params.sort, params.language, params.topic, params.includePrivate, params.search, githubToken])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
